import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { authService } from '../auth/authService';
import { verifyOtpSchema, VerifyOtpFormData } from '../validations/auth';

export default function VerifyOtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);

  useEffect(() => {
    // Get email from navigation state or redirect back to forgot password
    if (location.state?.email) {
      setEmail(location.state.email);
      // Start initial countdown when component loads
      startCountdown();
    } else {
      navigate('/forgot-password', { replace: true });
    }
  }, [location.state, navigate]);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useForm<VerifyOtpFormData>({
    resolver: yupResolver(verifyOtpSchema),
    defaultValues: {
      email: email,
      otp: '',
    },
  });

  // Update form when email is set
  useEffect(() => {
    if (email) {
      setValue('email', email);
    }
  }, [email, setValue]);

  // Handle OTP input changes
  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtpValues = [...otpValues];
      newOtpValues[index] = value;
      setOtpValues(newOtpValues);
      
      // Update form value
      const otpString = newOtpValues.join('');
      setValue('otp', otpString);
      
      // Auto-focus next input
      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Start countdown timer
  const startCountdown = () => {
    setCanResend(false);
    setResendCountdown(60); // 60 seconds = 1 minute
  };

  // Countdown effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);
    } else if (resendCountdown === 0 && !canResend) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown, canResend]);

  const onSubmit = async (data: VerifyOtpFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await authService.verifyOtp({
        email: data.email,
        otp: data.otp,
      });

      if (response.success && response.resetToken) {
        setSuccessMessage('OTP verified successfully! Redirecting to password reset...');
        // Navigate to recover password page with reset token
        setTimeout(() => {
          navigate('/recover-password', { 
            state: { 
              email: data.email, 
              resetToken: response.resetToken 
            },
            replace: true 
          });
        }, 2000);
      } else {
        setErrorMessage(response.message || 'Failed to verify OTP. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Verify OTP error:', error);
      
      // Handle specific error types
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      
      if (axiosError.response?.status === 400) {
        setError('otp', {
          type: 'manual',
          message: 'Invalid OTP code. Please check and try again.',
        });
      } else if (axiosError.response?.status === 422) {
        setError('root', {
          type: 'manual',
          message: 'Please check your input and try again.',
        });
      } else if (axiosError.response?.status && axiosError.response.status >= 500) {
        setErrorMessage('Server error. Please try again later.');
      } else {
        setErrorMessage(axiosError.response?.data?.message || 'Failed to verify OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await authService.forgotPassword({
        email: email,
      });

      if (response.success) {
        setSuccessMessage('New verification code has been sent to your email.');
        startCountdown(); // Start countdown after successful resend
      } else {
        setErrorMessage('Failed to resend verification code. Please try again.');
      }
    } catch {
      setErrorMessage('Failed to resend verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!email) {
    return null; // Don't render until email is loaded
  }

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 8,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('auth.verificationCodeTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {t('auth.verificationCodeDescription')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('auth.verificationCodeValidity')}
          </Typography>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* OTP Input Fields */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
            {otpValues.map((value, index) => (
              <TextField
                key={index}
                type="number"
                inputRef={(el) => (otpRefs.current[index] = el)}
                value={value}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                variant="outlined"
                size="medium"
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }
                }}
                sx={{
                  width: '52px',
                  height: '46px',
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    height: '60px',
                    '& input': {
                      padding: '16px 8px',
                    }
                  }
                }}
                disabled={isLoading}
                autoComplete="one-time-code"
              />
            ))}
          </Box>

          {errors.otp && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.otp.message}
            </Alert>
          )}

          {errors.root && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.root.message}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: '#b8957b',
              color: 'white',
              fontWeight: 700,
              '&:hover': { bgcolor: '#a98c74' },
              '&:disabled': { bgcolor: '#ccc' },
              mb: 2
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('auth.verifyOtp')
            )}
          </Button>
        </form>

        <Box textAlign="center" mt={2}>
          <Button
            onClick={handleResendOtp}
            disabled={isLoading || !canResend}
            sx={{
              textDecoration: 'none',
              color: canResend ? '#b8957b' : '#999',
              '&:hover': {
                textDecoration: canResend ? 'underline' : 'none',
              },
              '&:disabled': {
                color: '#999',
              },
            }}
          >
            {canResend ? t('auth.resendCode') : t('auth.resendIn').replace('{{count}}', resendCountdown.toString())}
          </Button>
        </Box>

        <Box textAlign="center" mt={1}>
          <Link
            href="/forgot-password"
            variant="body2"
            sx={{
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {t('auth.backToForgotPassword')}
          </Link>
        </Box>
      </Box>
    </Container>
  );
}
